/**
 * <park-rule> — ONE item of the park's information board: a circular icon
 * badge, a short shouted title, and an explanatory body (spec
 * `board-visual-language` FR-1; anton/forrest#1402).
 *
 * Lifted from the physical board that stands at the park: every rule there is
 * announced by a pictogram in a filled dark-green circle, with an uppercase
 * green title above a calm sentence of body copy.
 *
 * Single source of truth = the pure `renderParkRule()` below, consumed by BOTH
 * render contexts (live site via `components/ds/ParkRule.astro`, Claude-Design
 * preview via the custom element), exactly as `park-section-card` does.
 *
 * Degradation (spec edge cases): a missing title renders body-only (this is
 * what today's flat "one rule per line" content becomes — US-5), a missing body
 * renders title-only, and neither case leaves an empty element or a stray
 * separator behind.
 *
 * Attributes:
 *   - icon="…"    icon name from the fixed set (see ./board-icons.ts);
 *                 unknown/missing → the default paw (FR-4).
 *   - title="…"   short shouted title (optional).
 *   - body="…"    explanatory copy (optional; also accepted as slotted HTML).
 *
 * Emits `.board-rule/.board-rule__badge/.board-rule__text/.board-rule__title/
 * .board-rule__body` (CSS in ./css/park-rule-board.css).
 *
 * @dsSpec board-visual-language#FR-1 — the board's icon-led guidance item.
 */

import { ParkElement, defineElement, esc, html } from "./base";
import { boardIconSvg } from "./board-icons";

const TAG = "park-rule";

export interface ParkRuleProps {
	icon?: string;
	title?: string;
	body?: string;
}

/**
 * Pure render: (props, slotted body HTML) → inner HTML string. No DOM access,
 * so it is safe server-side (the .astro wrapper) and client-side (the element).
 * `bodyHtml` is trusted author/server markup and wins over the `body` attribute
 * when both are present.
 */
export function renderParkRule(props: ParkRuleProps, bodyHtml = ""): string {
	const title = (props.title ?? "").trim();
	const bodyText = (props.body ?? "").trim();
	const body = bodyHtml.trim() || (bodyText ? esc(bodyText) : "");

	const titleEl = title
		? html`<p class="board-rule__title">${esc(title)}</p>`
		: "";
	const bodyEl = body ? html`<p class="board-rule__body">${body}</p>` : "";

	return html`<span class="board-rule__badge">${boardIconSvg(props.icon)}</span><div class="board-rule__text">${titleEl}${bodyEl}</div>`;
}

class ParkRule extends ParkElement {
	static get observedAttributes(): string[] {
		return ["icon", "title", "body"];
	}

	protected render(): string {
		return renderParkRule(
			{
				icon: this.attr("icon"),
				title: this.attr("title"),
				body: this.attr("body"),
			},
			this.slotHtml,
		);
	}
}

defineElement(TAG, ParkRule);

export { ParkRule };
